import { FormLayout } from "@careinfo/layout";
import { AccessControl } from "@careinfo/permission-required";
import { tw } from "@careinfo/tailwind";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import { ColumnDef } from "@tanstack/react-table";
import { ClipboardEvent, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import APIS from "../../constant/EndPoint";
import { createRtvBranchValidationForm } from "../../utils/validationSchemas";
import Card from "../Reusable/Card";
import ComboboxInput from "../Reusable/ComboboxInput";
import DataTable from "../Reusable/DataTable";
import NACell from "../Reusable/DataTable/NACell";
import TrackingCell from "../Reusable/DataTable/TrackingCell";
import Spinner from "../Reusable/Spinner";
import TextField from "../Reusable/TextInput";
import ChangeStatus from "./ChangeStatus";

const EditReturnBranch = (props: any) => {
  const [searchParams, setSearchParms] = useSearchParams({
    limit: "10",
    page: "0",
    searchQuery: "",
  });
  const [statusData, setStatusData] = useState(null);

  const {
    handleEdit,
    isLoading,
    data,
    handleAttachConsignment,
    handlePrint,
    handleToggleMarkAsDone,
    isPrintLoading,
    handlecheckConsignments,
    gridData,
    handleStatusChange,
    defaultValue,
  } = props;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
    setValue,
    getValues,
    clearErrors,
  } = useForm<any>({
    resolver: yupResolver(createRtvBranchValidationForm),
    defaultValues: async () => {
      return {
        destination_branch: {
          name: data?.destination_branch?.name,
          id: data?.destination_branch?.id,
        },
        note: data?.note,
      };
    },
  });

  const checkedAllItems = searchParams.get("selectAllItems") === "1";
  const isSelected = (value: string) => {
    const selectedItems = searchParams.get("selectedItems")?.split(",");
    if (!selectedItems) {
      return false;
    }
    return selectedItems.includes(value);
  };

  const handleKeyDown = (event: any) => {
    if (event.key === "Enter" && event.target.value?.length > 0) {
      if (!data?.is_active) {
        handlecheckConsignments(event.target.value, setError);
      } else if (statusData?.length > 1 && defaultValue?.length === 1) {
        handleAttachConsignment(
          event.target.value,
          defaultValue[0]?.id,
          setError
        );
      } else if (statusData?.length === 1) {
        handleAttachConsignment(
          event.target.value,
          statusData[0]?.id,
          setError
        );
      } else {
        toast.error("Please select one status first");
      }
      event.target.value = "";
    }
  };

  const handleIndividualSelect = (value: string) => {
    let selectedItems = searchParams.get("selectedItems")?.split(",");
    if (!selectedItems || !selectedItems === undefined) {
      searchParams.set("selectedItems", value);
      setSearchParms(searchParams);
      return;
    }
    let itemIndex = selectedItems?.findIndex((item) => item === value);
    if (itemIndex > -1) {
      selectedItems.splice(itemIndex, 1);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      if (newSelectedItems.length) {
        searchParams.set("selectedItems", newSelectedItems);
      } else {
        searchParams.delete("selectedItems");
      }
      searchParams.set("selectAllItems", "0");
      setSearchParms(searchParams);
    } else {
      selectedItems?.push(value);
      const newSelectedItems = selectedItems
        ?.map((item) => item)
        .join(",")
        .toString();
      searchParams.set("selectedItems", newSelectedItems);
      setSearchParms(searchParams);
    }
  };

  const handleSelectAll = (table: any) => {
    const {
      options: { data: tableData },
    } = table;
    const isSelected = searchParams.get("selectAllItems");
    if (isSelected === "1") {
      searchParams.set("selectAllItems", "0");
      searchParams.delete("selectedItems");
      setSearchParms(searchParams);
    } else {
      const selectedItems = tableData
        ?.map((item: any) => item.id)
        .join(",")
        .toString();
      searchParams.set("selectAllItems", "1");
      searchParams.set("selectedItems", selectedItems);
      setSearchParms(searchParams);
    }
  };

  const defaultColumns: ColumnDef<any, any>[] = [
    {
      id: "select",
      header: ({ table }) => {
        return (
          <input
            type="checkbox"
            checked={checkedAllItems}
            onChange={() => handleSelectAll(table)}
            className={tw`cursor-pointer`}
          />
        );
      },
      cell: ({ cell: { row } }) => (
        <input
          type="checkbox"
          checked={isSelected(row?.original?.id)}
          onChange={() => handleIndividualSelect(row?.original?.id)}
          className={tw`cursor-pointer`}
        />
      ),
    },
    {
      header: "WayBill",
      accessorKey: "order.tracking_number",
      cell: TrackingCell,
    },
    {
      header: "Origin",
      accessorKey: "order.pickup_branch.name",
      cell: NACell,
    },

    {
      header: "Customer",
      accessorKey: "order.receiver.full_name",
      cell: ({ cell: { row, getValue } }) => {
        return (
          <>
            <p className={tw`uppercase`}>{getValue()}</p>
            <p className={tw`text-xs`}>
              {row?.original?.order?.receiver?.primary_contact}
            </p>
            <p className={tw`text-xs`}>
              {row?.original?.order?.receiver?.address}
            </p>
          </>
        );
      },
    },

    {
      header: "Destination",
      accessorKey: "order.destination.name",
      cell: NACell,
    },
    {
      header: "Weight",
      accessorKey: "order.weight",
      cell: ({ cell: { row, getValue } }) => {
        return (
          <>
            <p>
              {row?.original?.order?.package_type?.unit === "Gm"
                ? Number(getValue() * 1000) || 0
                : getValue() || 0}
              <span className="text-gray-500 text-xs">
                {row?.original?.order?.package_type?.unit}
              </span>
            </p>
          </>
        );
      },
    },
  ];

  const preventCopyPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    toast.error("Copy and paste is not allowed here");
  };

  useEffect(() => {
    if (Array.isArray(gridData)) {
      gridData?.map((x) => {
        if (x?.status === "intermediate") {
          setStatusData(x?.status_id);
        }
      });
    }
  }, [gridData]);

  return (
    <>
      <AccessControl
        children={
          <FormLayout
            handleSubmit={handleSubmit}
            handleFormSubmit={handleEdit}
            setError={setError}
            buttonLabel={"Update"}
            isLoading={isLoading}
            disabled={!data?.is_active}
          >
            <div className={tw`grid grid-cols-2 gap-x-4 mx-4`}>
              <ComboboxInput
                url={APIS.BRANCH}
                queryKey={"branch"}
                register={register("destination_branch")}
                errors={errors?.destination_branch}
                name={"destination_branch"}
                label={"Branch"}
                setValue={setValue}
                placeholder={"Search branch"}
                getValues={getValues}
                clearErrors={clearErrors}
                disabled={!data?.is_active}
              />
              <TextField
                label={"Note"}
                register={register(`note`, { disabled: !data?.is_active })}
                errors={errors?.note}
              />
            </div>
          </FormLayout>
        }
        allowedPermissions={["can_editupdate_runsheet"]}
        renderNoAccess={
          <FormLayout
            handleSubmit={handleSubmit}
            handleFormSubmit={handleEdit}
            setError={setError}
            buttonLabel={"Update"}
            isLoading={isLoading}
            disabled={true}
          >
            <div className={tw`grid grid-cols-2 gap-x-4 mx-4`}>
              <ComboboxInput
                url={APIS.BRANCH}
                queryKey={"branch"}
                disabled={true}
                register={register("destination_branch")}
                errors={errors?.destination_branch}
                name={"destination_branch"}
                label={"Branch"}
                setValue={setValue}
                placeholder={"Search branch"}
                getValues={getValues}
                clearErrors={clearErrors}
              />
              <TextField
                label={"Note"}
                disabled={true}
                register={register(`note`, { disabled: !data?.is_active })}
                errors={errors?.note}
              />
            </div>
          </FormLayout>
        }
      />
      <Card title="Barcode Scanner">
        <>
          <div className={tw`flex flex-col relative -top-6 items-end w-full`}>
            <ChangeStatus
              handleAttachConsignment={handleAttachConsignment}
              gridData={statusData}
              handleStatusChange={handleStatusChange}
            />

            <div className={tw`grid grid-cols-1 w-full`}>
              <div>
                <input
                  type="text"
                  className={tw`w-full focus:outline-none bg-[#EEF5F4] p-2 mt-1`}
                  onPaste={preventCopyPaste}
                  onKeyDown={handleKeyDown}
                />
                {errors?.tracking_number && (
                  <p className={tw`text-red-500 text-xs my-1`}>
                    {errors?.tracking_number?.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        </>
      </Card>
      <DataTable
        url={APIS.RTV_BRANCH_ITEMS}
        queryKey={"rtv-branch-items"}
        columns={defaultColumns}
        params={{ rtv_branch_order: data?.id }}
        tableName={"rtv-return"}
        actionMenu={
          <div className={tw`w-full text-right`}>
            {data?.is_active ? (
              <button
                className={tw`p-1 px-5 rounded-sm border border-primary mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1 `}
                onClick={handleToggleMarkAsDone}
              >
                Mark As Done
              </button>
            ) : (
              ""
            )}
            <button
              className={tw`p-1 px-5 rounded-sm border border-primary mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1 `}
              disabled={isPrintLoading}
              onClick={() => handlePrint()}
            >
              {isPrintLoading ? <Spinner /> : "Print"}
            </button>
          </div>
        }
      />
    </>
  );
};

export default EditReturnBranch;
